import { z, ZodIssueCode } from 'zod';
import { getDependentFieldOptionalSchema, getDependentFieldSchema, photoOptionalSchema } from '../../../constants/sharedSchemas';

const petsSchema = z.array(
  z.object({
    name: z.string().min(1, 'Pet name is required'),
    type: z.string().min(1, 'Pet type is required'),
    size: z.string().min(1, 'Pet size is required'),
    breed: z.string().min(1, 'Pet breed is required'),
    whereIsPetHoused: z.array(z.string()).min(1, 'Please specify'),
    photo: photoOptionalSchema,
  }),
);

const petsOptionalSchema = z.array(
  z.object({
    name: z.string().optional(),
    type: z.string().optional(),
    size: z.string().optional(),
    breed: z.string().optional(),
    whereIsPetHoused: z.array(z.string()).optional(),
    photo: photoOptionalSchema,
  }),
);

// Schema for when noPets is false (fields are required)
const hasPetsSchema = z.object({
  noPets: z.literal(false),
  pets: petsSchema,
});

// Schema for when noPets is true (fields are optional)
const noPetsSchema = z.object({
  noPets: z.literal(true),
  pets: petsOptionalSchema,
});

const petsSectionSchema = z.discriminatedUnion('noPets', [hasPetsSchema, noPetsSchema]);

export const houseHomeSchema = {
  required: z.object({
    housingType: z.object({
      typeOfHouse: z.string().min(1, 'Type of house is required'),
      areaYouLiveIn: z.string().min(1, 'Area is required'),
      homeDescription: z.string().min(1, 'Home description is required'),
      isBusiness: z
        .object({
          value: z.string().min(1, 'Please specify'),
          type: z.array(z.string()).optional(),
          other: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          // Make type required if the value is yes
          if (data.value === 'yes' && data.type?.length === 0) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: 'Type is required',
              path: ['type'],
            });
          }

          // Make other field required if the value is yes and the type is other
          if (data.value === 'yes' && data.type?.includes('Other') && !data.other?.trim()) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: 'Please specify',
              path: ['other'],
            });
          }
          return true;
        }),
    }),

    petsOwned: petsSectionSchema,

    activityLevel: z.object({
      householdActivityLevel: z.string().min(1, 'Please specify'),
      amenitiesAccess: z.array(z.string()).min(1, 'Amenities is required'),
      utilitiesAccess: z
        .object({
          value: z.array(z.string()).min(1, 'Utilities is required'),
          other: z.string().optional(),
        })
        .refine(
          (data) => {
            // Make other field required if the value includes other
            if (data.value.includes('Other')) {
              return data.other?.trim() !== '';
            }
            return true;
          },
          {
            message: 'Please specify',
            path: ['other'],
          },
        ),
      languageSpokenAtHome: getDependentFieldSchema('other', 'Other'),
      ownBedroom: getDependentFieldSchema('explanation', 'No'),
    }),

    reasons: z.object({
      discoverySource: z
        .object({
          value: z.array(z.string()).min(1, 'Source is required'),
          other: z.string().optional(),
        })
        .refine(
          (data) => {
            // Make other field required if the value includes other
            if (data.value.includes('Other')) {
              return data.other?.trim() !== '';
            }
            return true;
          },
          {
            message: 'Please specify',
            path: ['other'],
          },
        ),
      reason: z.string().min(1, 'This field is required'),
      expectations: z.string().min(1, 'This field is required'),
      specialActivities: z.string().min(1, 'This field is required'),
      studentResponsibilities: z.string().min(1, 'This field is required'),
      hostedPreviously: getDependentFieldSchema('explanation', 'yes'),
      isLocalCoordinator: getDependentFieldSchema('explanation', 'yes'),
      isLocalCoordinatorGH: z.string().min(1, 'Please specify'),
    }),
  }),
  optional: z.object({
    housingType: z.object({
      typeOfHouse: z.string().optional(),
      areaYouLiveIn: z.string().optional(),
      homeDescription: z.string().optional(),
      isBusiness: z.object({
        value: z.string().optional(),
        type: z.array(z.string()).optional(),
        other: z.string().optional(),
      }),
    }),

    petsOwned: z.object({ noPets: z.boolean(), pets: petsOptionalSchema }),

    activityLevel: z.object({
      householdActivityLevel: z.string().optional(),
      amenitiesAccess: z.array(z.string()).optional(),
      utilitiesAccess: z.object({
        value: z.array(z.string()).optional(),
        other: z.string().optional(),
      }),
      languageSpokenAtHome: getDependentFieldOptionalSchema('other'),
      ownBedroom: getDependentFieldOptionalSchema('explanation'),
    }),

    reasons: z.object({
      discoverySource: z.object({
        value: z.array(z.string()).optional(),
        other: z.string().optional(),
      }),
      reason: z.string().optional(),
      expectations: z.string().optional(),
      specialActivities: z.string().optional(),
      studentResponsibilities: z.string().optional(),
      hostedPreviously: getDependentFieldOptionalSchema('explanation'),
      isLocalCoordinator: getDependentFieldOptionalSchema('explanation'),
      isLocalCoordinatorGH: z.string().optional(),
    }),
  }),
};

export type HouseHomeFormValues = z.infer<(typeof houseHomeSchema)['required']> | z.infer<(typeof houseHomeSchema)['optional']>;
type Pet = HouseHomeFormValues['petsOwned']['pets'][number];

export const petDefaultValues: Pet = { name: '', type: '', size: '', breed: '', whereIsPetHoused: [], photo: { id: '' } };

export const defaultValues: HouseHomeFormValues = {
  // Housing Type
  housingType: {
    typeOfHouse: '',
    areaYouLiveIn: '',
    homeDescription: '',
    isBusiness: { value: '', type: [], other: '' },
  },

  //   Pets Owned
  petsOwned: {
    noPets: false,
    pets: [petDefaultValues],
  },

  // Activity Level
  activityLevel: {
    householdActivityLevel: '',
    amenitiesAccess: [],
    utilitiesAccess: { value: [], other: '' },
    languageSpokenAtHome: { value: '', other: '' },
    ownBedroom: { value: '', other: '' },
  },

  // Reasons for Hosting
  reasons: {
    discoverySource: { value: [], other: '' },
    reason: '',
    expectations: '',
    specialActivities: '',
    studentResponsibilities: '',
    hostedPreviously: { value: '', explanation: '' },
    isLocalCoordinator: { value: '', explanation: '' },
    isLocalCoordinatorGH: '',
  },
};
